/**
 * Метки: обычный тег доски, системный статус ОАП, приоритет, тип задачи.
 *
 * §10 и §31 требуют не смешивать обычные теги и системные статусные:
 * они различаются не только цветом, но и формой — у системного есть точка,
 * и он не удаляется с задачи вручную.
 */

import { X } from "lucide-react";

import { cn } from "./cn";

export type TagTone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

const TONES: Record<TagTone, string> = {
  neutral: "bg-neutral-soft text-muted",
  accent: "bg-accent-soft text-accent",
  success: "bg-success-soft text-success",
  warning: "bg-warning-soft text-warning",
  danger: "bg-danger-soft text-danger",
  info: "bg-info-soft text-info",
};

interface TagProps {
  children: string;
  tone?: TagTone;
  /** Произвольный цвет тега доски: он задаётся пользователем, а не палитрой.
   *  undefined в типе явно — цвет часто приходит из необязательного поля. */
  color?: string | null | undefined;
  onRemove?: () => void;
  size?: "sm" | "md";
  className?: string;
}

export function Tag({ children, tone = "neutral", color, onRemove, size = "md", className }: TagProps) {
  const custom = typeof color === "string" && color.length > 0;

  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-full font-semibold",
        size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-0.5 text-xs",
        !custom && TONES[tone],
        className,
      )}
      style={custom ? { backgroundColor: `${color}22`, color } : undefined}
    >
      <span className="truncate">{children}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Убрать тег ${children}`}
          className="-mr-0.5 shrink-0 rounded-full opacity-60 hover:opacity-100"
        >
          <X className="size-3" aria-hidden />
        </button>
      )}
    </span>
  );
}

/**
 * Системный статусный тег ОАП. Отдельный компонент намеренно: §10.2 запрещает
 * определять статус по обычному тегу, и в интерфейсе они тоже не должны
 * выглядеть взаимозаменяемыми.
 */
export function StatusTag({ label, code }: { label: string; code: string }) {
  const tone: TagTone =
    code === "completed"
      ? "success"
      : code === "in_progress"
        ? "info"
        : code === "on_control"
          ? "warning"
          : "neutral";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-bold",
        TONES[tone],
      )}
      title="Системный статус"
    >
      <span className="size-1.5 shrink-0 rounded-full bg-current" aria-hidden />
      {label}
    </span>
  );
}

/** Приоритет. Порядок и цвета приходят из справочника, а не зашиты в код (§7.3). */
export function PriorityBadge({
  name,
  color,
}: {
  name: string;
  color?: string | null | undefined;
}) {
  const custom = typeof color === "string" && color.length > 0;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-xs font-semibold",
        !custom && "text-muted",
      )}
      style={custom ? { color } : undefined}
    >
      <span
        className="size-2 shrink-0 rounded-[2px] bg-current"
        aria-hidden
      />
      {name}
    </span>
  );
}
